import Checkbox from 'components/Core/Checkbox/Checkbox'

import Filter from './baseFilter'

/*
 * BooleanCheckboxFilter
 * A standalone boolean checkbox rendered inline within a filter panel.
 * It stores its value directly under filterKey in the selected/applied filters:
 * {
 *  [filterKey]: true / false
 * }
 *
 * Unlike BooleanFilter (which renders as a modifier in the accordion title),
 * this one renders an actual checkbox in the panel body and participates in the
 * standard selectedFilters -> Apply Filters -> appliedFilters flow.
 *
 * Options:
 * - exclusiveGroupFilterKeys: filters in the same group are mutually exclusive;
 *   selecting one clears the others, and clicking the selected option deselects it.
 * - getLabel: optional ({ flags }) => string for dynamic checkbox labels, also used
 *   to label the pill when the filter is selected.
 * - pillGroupLabel: optional label prefixed to the pill, e.g. "Connection Status: Unassigned".
 */
export default class BooleanCheckboxFilter extends Filter {
  constructor({
    defaultState,
    styles,
    exclusiveGroupFilterKeys = null,
    getLabel = null,
    pillGroupLabel = null,
    ...rest
  }) {
    super(rest)
    this.unselectedState = false
    if (defaultState !== undefined) {
      this.defaultState = defaultState
    }
    this.styles = styles
    this.exclusiveGroupFilterKeys = exclusiveGroupFilterKeys
    this.getLabel = getLabel
    this.pillGroupLabel = pillGroupLabel
  }

  resolveLabel = (context = {}) =>
    this.getLabel ? this.getLabel(context) : this.label

  getState = (selectedFilters) => Boolean(selectedFilters[this.filterKey])

  renderFilterPanelHandler = ({
    selectedFilters,
    actionHandler,
    flags = {},
  }) => ({
    key: this.dataKey,
    title: null,
    expanded: true,
    content: (
      <Checkbox
        checked={this.getState(selectedFilters)}
        onClick={() => actionHandler({})}
        label={this.resolveLabel({ flags })}
        className={this.styles && this.styles.checkbox}
      />
    ),
    showFilterHandler: this.showFilterHandler,
  })

  isUnselected = (filterItem) => !filterItem

  renderPillLabelHandler = (filterInfo, dateFormat, flags = {}) => {
    const label = this.resolveLabel({ flags })
    if (!this.pillGroupLabel) {
      return label
    }
    return (
      <span>
        {this.pillGroupLabel}: {label}
      </span>
    )
  }

  toGraphQLVariable = (graphQLFilters, { value }) => ({
    ...graphQLFilters,
    [this.filterKey]: Boolean(value),
  })

  reducerHandler = (selectedFilters) => {
    const currentValue = Boolean(selectedFilters[this.filterKey])
    const newValue = !currentValue

    if (!this.exclusiveGroupFilterKeys) {
      return {
        ...selectedFilters,
        [this.filterKey]: newValue,
      }
    }

    const groupUpdates = this.exclusiveGroupFilterKeys.reduce(
      (accumulator, filterKey) => ({
        ...accumulator,
        [filterKey]: filterKey === this.filterKey ? newValue : false,
      }),
      {},
    )

    return {
      ...selectedFilters,
      ...groupUpdates,
    }
  }

  addToActiveFilters = (currentState, filterState) => {
    if (this.isUnselected(filterState)) {
      return currentState
    }
    return [...currentState, { dataKey: this.dataKey }]
  }
}
